/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class EmailService {
  maxEmailLength = 254;
  maxDomainLength = 190;
  emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  domainRegex = new RegExp(
    /^(?=^.{4,190}$)(((?!-))(xn--)?[a-zA-Z0-9\-_]{0,61}[a-zA-Z0-9]{1,1}\.)*(xn--)?([a-zA-Z0-9-]{1,61}|[a-zA-Z0-9-]{1,30})\.[a-zA-Z]{2,}$/,
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  replaceComma(str: string) {
    const regex = /([a-z]),|([A-Z]),|(\d),/gm;
    const subst = `$1;`;

    return str.replace(regex, subst);
  }

  cleanAddressList(str: string) {
    let stringToClean = str;
    if (!stringToClean) {
      return str;
    }
    // replace all , with ;
    stringToClean = this.replaceComma(stringToClean);

    // just let it go if the end of string is '; ''
    const last2 = stringToClean.slice(-2);
    if (last2 === '; ') {
      return stringToClean;
    }

    // don't start with space, ; or ,
    if (
      stringToClean.length === 1 &&
      (stringToClean.endsWith(' ') ||
        stringToClean.endsWith(';') ||
        stringToClean.endsWith(','))
    ) {
      // remove the last char
      stringToClean = stringToClean.slice(0, -1);
      return stringToClean;
    }

    // don't keep double end separators
    if (
      last2 === ';;' ||
      last2 === ',,' ||
      last2 === ',;' ||
      last2 === ';,' ||
      last2 === ' ;' ||
      last2 === ' ,' ||
      last2 === '  '
    ) {
      // remove the last char
      stringToClean = stringToClean.slice(0, -1);
      return stringToClean;
    }

    // replace the space with ;
    if (stringToClean.endsWith(' ')) {
      // replace the space with ; and a space.
      stringToClean = stringToClean.replace(/.$/, '; ');
    }

    return stringToClean;
  }

  isValidEmailAddress(email: string) {
    let emailArr;

    // validate email format and ensure that the username portion is no greater than 64 characters and the domain is no greater than 190 characters.
    if (new RegExp(this.emailRegex).test(email)) {
      emailArr = email.split('@');
      if (
        emailArr[0].length <= this.maxEmailLength - this.maxDomainLength &&
        emailArr[1].length <= this.maxDomainLength
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  isValidDomain(domain: string) {
    return !domain.length || (domain.length && !this.domainRegex.test(domain))
      ? false
      : true;
  }

  parseCSVFile(file: File | null, maxEmails = 0, csvSeparator = ',') {
    return new Promise<[string[], string | ArrayBuffer | null, string[]]>(
      (resolve, reject) => {
        if (file) {
          const fileReader = new FileReader();
          const fileToRead = file;
          const duplicatedEmails: string[] = [];
          fileReader.readAsText(fileToRead, 'UTF-8');
          fileReader.onload = (e) => {
            const textFromFileLoaded = e.target ? e.target.result : '';
            const lines = String(textFromFileLoaded).split('\n');
            const parsedEmails: string[] = [];

            lines.forEach((line) => {
              line = line.trim();
              if (line.length) {
                const cols: string[] = line.split(csvSeparator);
                if (cols.length) {
                  cols.forEach((email) => {
                    if (maxEmails) {
                      if (email.length && parsedEmails.length < maxEmails) {
                        parsedEmails.push(email.trim());
                      }
                    } else {
                      if (email.length) {
                        parsedEmails.push(email.trim());
                      }
                    }
                  });
                }
              }
            });
            // check for duplicates
            const unique = [];
            let uIndex = 0;
            parsedEmails.sort();
            unique.push(parsedEmails[0]);
            for (let i = 1; i < parsedEmails.length; i++) {
              if (unique[uIndex] === parsedEmails[i]) {
                duplicatedEmails.push(parsedEmails[i]);
              } else {
                unique.push(parsedEmails[i]);
                uIndex++;
              }
            }
            resolve([unique, textFromFileLoaded, duplicatedEmails]);
            fileReader.onerror = reject;
          };
          fileReader.onerror = () => reject(fileReader.error);
        }
      },
    );
  }
}
